import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Star } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

const ADD_KEY_EVENT = gql`
  mutation AddKeyEvent(
    $odrnumber: Int!
    $event: String!
    $startdate: Date!
    $enddate: Date
    $notes: String
  ) {
    createKeyEvent(
      keyEvent: {
        odrparticipant: $odrnumber
        event: $event
        startdate: $startdate
        enddate: $enddate
        notes: $notes
      }
    ) {
      message
      responseCode
    }
  }
`;

const eventTypes = [
  { value: "hospitalized", label: "Hospitalized", hasEndDate: true },
  { value: "awol", label: "AWOL", hasEndDate: true },
  { value: "graduated", label: "Graduated", hasEndDate: false },
  { value: "enrolled", label: "Enrolled", hasEndDate: false },
  { value: "court_appearance", label: "Court Appearance", hasEndDate: false },
  { value: "violation", label: "Violation", hasEndDate: false },
  { value: "treatment_started", label: "Treatment Started", hasEndDate: false },
  { value: "treatment_completed", label: "Treatment Completed", hasEndDate: false },
  { value: "relapsed", label: "Relapsed", hasEndDate: false },
  { value: "other", label: "Other", hasEndDate: true },
];

interface KeyEventEntryFormProps {
  odrNumber: string;
  participantId: string;
  user: any;
  showHeader?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const KeyEventEntryForm: React.FC<KeyEventEntryFormProps> = ({
  odrNumber,
  showHeader = true,
  onSuccess,
  onCancel
}) => {
  const [eventType, setEventType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [addKeyEvent, { loading }] = useMutation(ADD_KEY_EVENT, {
    onCompleted: () => {
      onSuccess();
    },
    onError: (error) => {
      console.error("Error adding key event:", error);
      setErrors({ submit: "Failed to add key event. Please try again." });
    },
  });

  const selectedEventType = eventTypes.find(et => et.value === eventType);
  const showEndDate = selectedEventType?.hasEndDate ?? false;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!eventType) {
      newErrors.eventType = "Event type is required";
    }
    
    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }
    
    if (showEndDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = "End date must be after start date";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const eventLabel = selectedEventType?.label || eventType;
    const effectiveEndDate = showEndDate ? endDate : startDate;

    await addKeyEvent({
      variables: {
        odrnumber: parseInt(odrNumber),
        event: eventLabel,
        startdate: startDate,
        enddate: effectiveEndDate || null,
        notes: notes || null,
      },
      refetchQueries: ["GetParticipantByODRNumber", "GetSummaryData"],
    });
  };

  return (
    <div className={showHeader ? "space-y-4 py-4" : "space-y-4 pb-4"}>
      {showHeader && (
        <SectionHeader
          icon={Star}
          title="Key Event"
          subtitle="Record milestones or critical events"
        />
      )}
      <div className="space-y-2">
        <Label htmlFor="eventType">Event Type</Label>
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger id="eventType">
            <SelectValue placeholder="Select event type" />
          </SelectTrigger>
          <SelectContent>
            {eventTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.eventType && (
          <p className="text-sm text-red-600">{errors.eventType}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startDate">
            {showEndDate ? "Start Date" : "Date"}
          </Label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          {errors.startDate && (
            <p className="text-sm text-red-600">{errors.startDate}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="endDate">End Date (Optional)</Label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={!showEndDate}
            className={!showEndDate ? "bg-gray-100 cursor-not-allowed text-gray-400" : ""}
          />
          {errors.endDate && (
            <p className="text-sm text-red-600">{errors.endDate}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any additional details..."
          rows={3}
        />
      </div>

      {errors.submit && (
        <p className="text-sm text-red-600">{errors.submit}</p>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t mt-4">
        <Button size="sm" onClick={handleSubmit} disabled={loading}>
          {loading ? "Adding..." : "Add Key Event"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
